"use client";

import type { MotionValue } from "@/components/LazyMotion";
import {
  AnimatePresence,
  motion,
  useScroll,
  useTransform,
} from "@/components/LazyMotion";
import React, { useEffect, useRef, useState } from "react";
import { ButtonStyleType, SpacingType, sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Image from "@/components/ui/Image";
import Link from "next/link";
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

type FeatureItem = {
  badge?: string;
  title?: string;
  content?: string;
  image?: string;
  imageAltText?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
};

interface Feature26Props {
  darkMode?: boolean;
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  attributes?: React.HTMLProps<HTMLElement>;
  items?: FeatureItem[];
}

interface AnimationWrapperProps {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  duration?: number;
}

const AnimationWrapper = ({
  children,
  delay = 0,
  className,
  duration = 0.35,
}: AnimationWrapperProps) => {
  return (
    <motion.div
      initial={{ x: 10, opacity: 0 }}
      animate={{ x: 0, opacity: 1, transition: { duration, ease: "easeOut", delay } }}
      // Exit a touch faster than enter so slides don't stack, but not abrupt
      exit={{ x: -10, opacity: 0, transition: { duration: 0.25, delay: 0 } }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

interface ScrollProgressIndicatorProps {
  totalFeatures: number;
  activeIndex: number;
  scrollYProgress: MotionValue<number>;
}

const ScrollProgressIndicator = ({
  totalFeatures,
  activeIndex,
  scrollYProgress,
}: ScrollProgressIndicatorProps) => {
  const start = activeIndex / totalFeatures;
  const end = (activeIndex + 1) / totalFeatures;
  const width = useTransform(scrollYProgress, [start, end], ["0%", "100%"]);

  return (
    <div className="mt-14 hidden w-fit items-center justify-center gap-3 rounded-full px-0 py-4 lg:flex">
      {Array.from({ length: totalFeatures }).map((_, index) => (
        <div
          key={`scroll-progress-indicator-${index}`}
          className={cn(
            "relative h-2 w-2 rounded-full bg-foreground/20 transition-[width] duration-200",
            activeIndex === index && "w-10",
            activeIndex > index && "bg-foreground",
          )}
        >
          {activeIndex === index && (
            <motion.div
              style={{ width }}
              className="absolute top-0 left-0 h-full rounded-full bg-foreground"
            />
          )}
        </div>
      ))}
    </div>
  );
};

const FeatureTopContent = ({ item }: { item: FeatureItem }) => (
  <div className="flex flex-col gap-2">
    {item.badge && (
      <AnimationWrapper>
        <Badge variant="outline" bold>
          {item.badge}
        </Badge>
      </AnimationWrapper>
    )}
    {item.title && (
      <AnimationWrapper delay={item.badge ? 0.1 : 0}>
        <h3>{parse(item.title)}</h3>
      </AnimationWrapper>
    )}
    {item.content && (
      <AnimationWrapper delay={0.15}>
        <div className="paragraph-base text-muted-foreground">
          {parse(item.content)}
        </div>
      </AnimationWrapper>
    )}
  </div>
);

const FeatureBottomContent = ({ item }: { item: FeatureItem }) => {
  if (!(item.hasButton && item.buttonText)) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1">
      {item.hasButton && item.buttonText && (
        <AnimationWrapper delay={0.25}>
          <Button
            variant={item.buttonStyle}
            size="lg"
            asChild
            className="mt-2 w-full sm:w-auto"
          >
            <Link href={item.buttonLink ?? ""}>{item.buttonText}</Link>
          </Button>
        </AnimationWrapper>
      )}
    </div>
  );
};

const FeatureVisual = ({
  item,
}: {
  item: FeatureItem;
  index: number;
}) => (
  <AnimationWrapper
    duration={0.45}
    className="flex h-96 w-full items-center gap-6 lg:h-full"
  >
    {item.image && (
      <Image
        src={item.image}
        alt={item.imageAltText || item.title || ""}
        title={item.imageAltText || item.title || ""}
        width={800}
        height={600}
        className={cn(
          "h-96 w-full overflow-hidden object-cover lg:h-full",
        )}
      />
    )}
  </AnimationWrapper>
);

/** Mobile stacked card — progress dots are desktop-only so omitted here */
const FeatureCardMobile = ({
  item,
  index,
}: {
  item: FeatureItem;
  index: number;
}) => (
  <div className="flex w-full flex-col-reverse justify-between gap-8 px-4 py-4 sm:px-6">
    <div className="flex flex-col gap-8">
      <FeatureTopContent item={item} />
      <FeatureBottomContent item={item} />
    </div>
    <FeatureVisual item={item} index={index} />
  </div>
);

interface DesktopStageProps {
  item: FeatureItem;
  index: number;
  totalFeatures: number;
  scrollYProgress: MotionValue<number>;
}

/** Desktop stage: progress indicator stays mounted outside AnimatePresence */
const DesktopStage = ({
  item,
  index,
  totalFeatures,
  scrollYProgress,
}: DesktopStageProps) => {
  return (
    <div className="flex h-full w-full flex-row items-center gap-8 px-10">
      <div className="flex h-full flex-2/5 flex-col">
        {/* Top content — swaps with slide */}
        <div className="relative min-h-[8rem]">
          <AnimatePresence mode="wait">
            <motion.div key={`top-${index}`}>
              <FeatureTopContent item={item} />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Persistent progress — never exits with the slide */}
        <ScrollProgressIndicator
          totalFeatures={totalFeatures}
          activeIndex={index}
          scrollYProgress={scrollYProgress}
        />

        {/* Bottom content — swaps with slide */}
        <div className="relative mt-auto min-h-[6rem]">
          <AnimatePresence mode="wait">
            <motion.div key={`bottom-${index}`}>
              <FeatureBottomContent item={item} />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className="relative h-full flex-3/5">
        <AnimatePresence mode="wait">
          <motion.div key={`visual-${index}`} className="h-full w-full">
            <FeatureVisual item={item} index={index} />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

const Feature26 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Feature 26",
  themeChangeOnScroll = false,
  attributes,
  items = [],
}: Feature26Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  const [activeIndex, setActiveIndex] = useState(0);
  const totalFeatures = items.length;

  useEffect(() => {
    if (!totalFeatures) return;

    const unsubscribe = scrollYProgress.on("change", (latest) => {
      const nextIndex = Math.min(
        totalFeatures - 1,
        Math.max(0, Math.floor(latest * totalFeatures)),
      );
      setActiveIndex(nextIndex);
    });

    return () => unsubscribe();
  }, [scrollYProgress, totalFeatures]);

  if (!totalFeatures) return null;

  const activeItem = items[activeIndex];

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature26"
      attributes={attributes}
      removePaddingTop
      removePaddingBottom
    >
      {/* Desktop: tall page-scroll track + sticky viewport */}
      <div
        ref={ref}
        className="relative hidden lg:block"
        style={{ height: `${totalFeatures * 100}vh` }}
      >
        <div className="sticky top-[var(--header-h)] flex h-[calc(100svh-var(--header-h))] w-full items-center">
          <div className="container m-0 h-3/5 w-full p-0!">
            <DesktopStage
              item={activeItem}
              index={activeIndex}
              totalFeatures={totalFeatures}
              scrollYProgress={scrollYProgress}
            />
          </div>
        </div>
      </div>

      {/* Mobile: stacked cards (no scroll-jacking) */}
      <div className="container flex w-full flex-col items-center justify-center gap-10 py-16 lg:hidden">
        {items.map((item, index) => (
          <FeatureCardMobile
            key={`feature-${index}`}
            index={index}
            item={item}
          />
        ))}
      </div>
    </SectionWrapper>
  );
};

export { Feature26 };
